import { omit } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import {
  func,
  type IValidationSchema,
  required,
  validationContext,
} from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { RoutingContextSourceEditorViewModelBase } from '@/features/legends/data-sources/RoutingContextSourceEditorViewModelBase'
import { Types } from '@/features/legends/editor/ryno-types'
import {
  mustHaveItems,
  validateWhileEditing,
} from '@/features/legends/editor/validation'
import type { ServiceItemPresenceSource } from '@/features/legends/data-sources/data-sources'
import type { ConjunctionOperator } from '@/features/legends/filters/rules'

/**
 * Whether the job has all (or any) of some service items.
 */
export class ServiceItemPresenceEditorViewModel extends RoutingContextSourceEditorViewModelBase<ServiceItemPresenceSource> {
  private readonly _schema: IValidationSchema<ServiceItemPresenceEditorViewModel> =
    {
      id: RoutingContextSourceEditorViewModelBase.idValidation.validators,
      inventoryItemIds: [
        mustHaveItems('Please select at least 1 inventory item.'),
      ],
      op: [
        required(),
        func(
          ({ value }) => value === 'Any' || value === 'All',
          'Not a valid op',
        ),
      ],
    }

  readonly type = 'ServiceItemPresence'
  readonly valueType = Types.boolean

  /**
   * The service items' IDs.
   */
  @observable accessor inventoryItemIds: string[] = []
  @observable accessor op: ConjunctionOperator = 'Any'

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies, 'SERVICE_ITEM_PRESENCE')
    // The ID is validated when it is committed.
    this.lifecycle.manage(validateWhileEditing(this, omit(this._schema, 'id')))
  }

  /**
   * The service items the legend's service type can use, as options to pick
   * from.
   */
  @computed
  get availableInventoryItems() {
    return this.dependencies.catalog.allowedInventoryItems.flatMap((i) =>
      i.id ? [{ id: i.id, text: `${i.name} - ${i.shortId}` }] : [],
    )
  }

  /**
   * Sets the service items by ID.
   */
  @action.bound
  setInventoryItems(ids: string[]) {
    this.inventoryItemIds = ids
  }

  /**
   * Sets whether the job needs all or any of the service items.
   */
  @action.bound
  setOp(op: ConjunctionOperator) {
    this.op = op
  }

  /** @inheritDoc */
  @action
  hydrate(source: ServiceItemPresenceSource) {
    super.hydrate(source)
    this.inventoryItemIds = source.inventoryItemIds ?? []
    this.op = source.op ?? 'Any'
  }

  /** @inheritDoc */
  toJSON(): ServiceItemPresenceSource {
    return {
      type: this.type,
      id: this.id,
      inventoryItemIds: this.inventoryItemIds.slice(),
      op: this.op,
    }
  }
}
